import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from "react-router-dom";
import { toast } from 'react-hot-toast';
import { HiMail } from 'react-icons/hi';

import { forgotPasswordEmail } from '../../redux/actions/user';
import { validateEmail } from '../../common/validations';

import { Button } from '../../components/buttons/Button';
import { CopyrightFooter } from '../../components/copyrightFooter/CopyrightFooter';
import { BrandingLogo } from '../../components/brandingLogo/BrandingLogo';

import image from '../../static/image/7.png';

export const FormForgetPasswordEmail = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [focusedInput, setFocusedInput] = useState(null);
    const [emailError, setEmailError] = useState(null);
    const [formData, setFormData] = useState({
        email: '',
    });

    const onChange = (e) => {
        const { name, value } = e.target;
        if (name === 'email') {
            setEmailError(null);
        }
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    }

    const validateForm = () => {
        const { email } = formData;
        const trimmed = email?.trim() ?? '';
        const emailValid = validateEmail(trimmed);

        if (!trimmed) {
            setEmailError('required');
            toast.error('Ingresa tu correo electrónico');
            setLoading(false);
            return false;
        }
        if (!emailValid) {
            setEmailError('invalid');
            toast.error('Ingresa un correo electronico valido');
            setLoading(false);
            return false;
        }
        setEmailError(null);
        return true;
    }

    const handleSendEmail = (e) => {
        e.preventDefault();
        setLoading(true);
        if (!validateForm()) {
            return;
        }
        const { email } = formData;
        dispatch(forgotPasswordEmail(email.trim()))
            .then((result) => {
                if (result.status === 200) {
                    toast.success(result.message);
                    navigate('/');
                }
                if (result.status === 400) {
                    toast.error(result.message);
                }
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    }

    const renderStep = () => (
        <div className='w-full h-full min-h-0 flex-1 flex flex-col bg-white animate-fadeIn'>
            <div className='w-full max-w-md mx-auto flex flex-col flex-1 min-h-0 px-4 md:px-6'>
                <div className='flex-1 flex flex-col justify-center min-h-0 overflow-y-auto py-6'>
                    <div className='flex justify-center shrink-0 mb-8 animate-slideDown'>
                        <BrandingLogo />
                    </div>
                    <div className='text-center mb-8 animate-slideUp'>
                        <h2 className='text-2xl font-bold text-gray-800 mb-2'>Recuperar contraseña</h2>
                        <p className='text-gray-500 text-sm'>
                            Ingresa tu correo electrónico y sigue las instrucciones que enviaremos a tu bandeja.
                        </p>
                    </div>
                    <form noValidate onSubmit={(e) => handleSendEmail(e)} className='space-y-6'>
                        <div className='relative animate-slideUp' style={{ animationDelay: '0.1s' }}>
                            <label
                                htmlFor='forget-password-email'
                                className={`block text-sm font-semibold mb-2 ${emailError ? 'text-red-700' : 'text-gray-700'}`}
                            >
                                Correo electrónico
                            </label>
                            <div className='relative'>
                                <div className={`absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none transition-colors duration-300 ${emailError ? 'text-red-500' : focusedInput === 'email' ? 'text-indigo-600' : 'text-gray-400'
                                    }`}>
                                    <HiMail className='w-5 h-5' />
                                </div>
                                <input
                                    id='forget-password-email'
                                    type='email'
                                    name='email'
                                    autoComplete='email'
                                    value={formData.email}
                                    onChange={onChange}
                                    onFocus={() => setFocusedInput('email')}
                                    onBlur={() => setFocusedInput(null)}
                                    placeholder='tucorreo@ejemplo.com'
                                    disabled={loading}
                                    aria-invalid={emailError ? 'true' : 'false'}
                                    className={`w-full pl-12 pr-4 py-3.5 border-2 rounded-xl transition-all duration-300 outline-none
                                        ${emailError
                                            ? 'border-red-500 bg-red-50 text-red-900 placeholder-red-300 focus:ring-4 focus:ring-red-100'
                                            : focusedInput === 'email'
                                                ? 'border-indigo-600 bg-indigo-50/50 shadow-lg shadow-indigo-200/50 focus:ring-4 focus:ring-indigo-100'
                                                : 'border-gray-200 bg-white hover:border-gray-300 focus:ring-4 focus:ring-indigo-100'
                                        }
                                        disabled:bg-gray-50 disabled:cursor-not-allowed`}
                                />
                            </div>
                            {emailError === 'required' && (
                                <p className='mt-2 text-sm text-red-600'>Ingresa tu correo electrónico.</p>
                            )}
                            {emailError === 'invalid' && (
                                <p className='mt-2 text-sm text-red-600'>El formato del correo no es válido.</p>
                            )}
                        </div>

                        <div className='animate-slideUp' style={{ animationDelay: '0.2s' }}>
                            <Button
                                type="submit"
                                variant="gradient"
                                fullWidth
                                loading={loading}
                                loadingLabel="Enviando..."
                            >
                                Solicitar nueva contraseña
                            </Button>
                        </div>
                        <div className='flex justify-center mt-6 animate-slideUp' style={{ animationDelay: '0.4s' }}>
                            <Link to={'/'} className='text-sm'>
                                <span className='text-gray-700'>¿Ya tienes una cuenta? </span>
                                <span className='font-semibold text-indigo-600 hover:text-indigo-800 transition-colors duration-300 hover:underline'>Iniciar sesión</span>
                            </Link>
                        </div>
                    </form>
                </div>
                <CopyrightFooter className='flex-shrink-0 pb-6 pt-8 animate-fadeIn' style={{ animationDelay: '0.3s' }} />
            </div>

            <style jsx>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slideDown {
                    from { opacity: 0; transform: translateY(-20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.6s ease-out forwards;
                }
                .animate-slideDown {
                    animation: slideDown 0.6s ease-out forwards;
                }
                .animate-slideUp {
                    animation: slideUp 0.6s ease-out forwards;
                    opacity: 0;
                    animation-fill-mode: forwards;
                }
            `}</style>
        </div>
    );

    return (
        <div className='w-full flex'>
            <div className='w-full h-screen md:w-[40%] flex flex-col min-h-0'>
                {renderStep()}
            </div>
            <div
                className='w-0 md:w-[60%] h-screen bg-cover'
                style={{ backgroundImage: `url(${image})` }}
            ></div>
        </div>
    );
};
